/**
 * extensions/dicom-rt/src/components/LongitudinalStudy/sections/FindingSection.js
 *
 * RECIP 1.0 F-1 — PSMA+ volume mode toggle.
 *
 * F-1 changes from E-4:
 *
 *   1. volumeMode prop added ('all' | 'psma-positive').
 *      Non-RECIP: ignored, 4 dropdowns unchanged.
 *      RECIP: 5th dropdown between Specific Link and Measurement Technique.
 *
 *   2. techniqueKeyMapping logic: when isPsmaMode (RECIP + psma-positive)
 *      and VOLUMETRIC, use 'sumPsmaPosVolume' instead of 'sumVolume'.
 *      BIDIMENSIONAL always uses 'sumDiameter' (no PSMA+ diameter analog).
 *
 *   3. addSign null fix: `isFinite(null) === true` in JS, so addSign(null)
 *      previously returned null (rendered as empty cell). Fixed: explicit
 *      null → 'NE' check before the isFinite guard.
 *
 *   4. fmtSum helper: renders the raw sum cell correctly for PSMA+ mode,
 *      where null = 'NE' (no liver ref), 0 = '0.0' (PSMA-cold, evaluable).
 *
 *   5. Column header changes to "PSMA+ Vol(mm³)" in PSMA+ mode.
 *
 *   6. Grid sizing: RECIP 5 cols at xs={2} (=10 total), non-RECIP 4 × xs={3}.
 */

import React from 'react';
import PropTypes from 'prop-types';

import {
  Body,
  Container,
  Grid,
  Select,
  H6,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@platform/ui';
import module from '../../../modules/dicom-measurement/src';
import getRecipTimepointStats from '../../../modules/dicom-measurement/src/assessments/getRecipTimepointStats';

const { guideline, getTimepoints, getTimepointStats } = module;
const {
  MEASUREMENT_TECHNIQUE,
  getMeasurementTechniques,
  getBodyParts,
  getTrackingTypes,
} = guideline;

/**
 * Fixed addSign: handles null explicitly (NE) before isFinite guard.
 * Old: isFinite(null) === true → addSign(null) returned null → empty cell.
 * New: null → 'NE' (red text rendered at call site).
 */
function addSign(num) {
  if (num === null) return 'NE';
  if (num === undefined || !isFinite(num)) return '—';
  return num > 0 ? `+${num}` : num;
}

/**
 * Render the raw "Sum" cell value.
 * In PSMA+ mode: null = NE, 0 = '0.0' (PSMA-cold but evaluable), number = formatted.
 * In All Volume mode: pass through as-is (existing behaviour).
 */
function fmtSum(v, isPsmaMode) {
  if (!isPsmaMode) return v;
  if (v === null) return <span style={{ color: '#ef9a9a' }}>NE</span>;
  if (v === undefined || !isFinite(v)) return '—';
  return v.toFixed(1);
}

function getUnit(measurementTechnique) {
  const mapping = {
    [MEASUREMENT_TECHNIQUE.BIDIMENSIONAL]: <span>Sum(mm)</span>,
    [MEASUREMENT_TECHNIQUE.VOLUMETRIC]: (
      <span>
        Sum(mm<sup>3</sup>)
      </span>
    ),
  };
  return mapping[measurementTechnique];
}

export const FindingSection = ({
  imageSets,
  ROIContours,
  criteria,
  assessments,
  getFilteredROIContours,
  getRoiUnionExcludingPsma, // accepted, unused in E-state
  filteredBodyPart,
  setFilteredBodyPart,
  filteredTrackingType,
  setFilteredTrackingType,
  filteredPsmaStatus,
  setFilteredPsmaStatus, // accepted, unused
  selectedLesionNumber,
  setSelectedLesionNumber,
  measurementTechnique,
  setMeasurementTechnique,
  volumeMode = 'all',
  setVolumeMode,
}) => {
  const isRecip = criteria?.guidelineId === 'RECIP10';
  const isPsmaMode = isRecip && volumeMode === 'psma-positive';

  const filteredROIContours = getFilteredROIContours(ROIContours);
  let timepoints, timepointStats;

  if (isRecip) {
    timepoints = getTimepoints(imageSets, filteredROIContours);
    timepointStats = getRecipTimepointStats(
      timepoints,
      filteredROIContours,
      [],
      criteria
    );
  } else {
    timepoints = getTimepoints(imageSets, filteredROIContours);
    timepointStats = getTimepointStats(
      timepoints,
      filteredROIContours,
      [],
      criteria,
      assessments
    );
  }

  // Key used to read the sum and change values from the stats objects
  const sumKey = (() => {
    if (measurementTechnique === MEASUREMENT_TECHNIQUE.BIDIMENSIONAL)
      return 'sumDiameter';
    return isPsmaMode ? 'sumPsmaPosVolume' : 'sumVolume';
  })();

  const reports = timepoints.map((timepoint, index) => ({
    timepoint,
    sum: timepointStats[index]?.summation?.[sumKey],
    fromBaseline: timepointStats[index]?.fromBaseline?.[sumKey],
    fromNadir: timepointStats[index]?.fromNadir?.[sumKey],
    fromLatest: timepointStats[index]?.fromLatest?.[sumKey],
  }));

  // Column header: swap to PSMA label in psma-positive mode
  const sumHeader = isPsmaMode ? (
    <span>
      PSMA+ Vol(mm<sup>3</sup>)
    </span>
  ) : (
    getUnit(measurementTechnique)
  );

  const onBodyPartChange = evt => setFilteredBodyPart(evt.target.value);
  const onTrackingTypeChange = evt => {
    const type = evt.target.value;
    setFilteredTrackingType(type);
    if (type !== 'selected') setSelectedLesionNumber(-1);
  };
  const onLesionNumberChange = evt =>
    setSelectedLesionNumber(Number(evt.target.value));
  const onTechniqueChange = evt => setMeasurementTechnique(evt.target.value);
  const onVolumeModeChange = evt => setVolumeMode?.(evt.target.value);

  return (
    <Body>
      <Container>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow index={-1}>
              {[
                ['No.', '6%'],
                ['Date', '14%'],
                [sumHeader, '20%'],
                ['from Baseline(%)', '20%'],
                ['from Nadir(%)', '20%'],
                ['from Latest(%)', '20%'],
              ].map(([text, width], i) => (
                <TableCell key={i} index={i} width={width} header={1}>
                  <H6>{text}</H6>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {reports.map((report, index) => (
              <TableRow key={report.timepoint.SeriesDate + index} index={index}>
                {[
                  index,
                  report.timepoint.SeriesDate,
                  fmtSum(report.sum, isPsmaMode),
                  addSign(report.fromBaseline),
                  addSign(report.fromNadir),
                  addSign(report.fromLatest),
                ].map((text, i) => (
                  <TableCell key={i} index={i}>
                    <H6>{text}</H6>
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Container>

      {/* Filter row: 5 cols [3,3,2,2,2]=12 for RECIP, 4 cols xs={3} for non-RECIP */}
      <Grid container spacing={2}>
        <Grid item xs={isRecip ? 3 : 3}>
          <Select value={filteredBodyPart} onChange={onBodyPartChange}>
            {getBodyParts().map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        </Grid>
        <Grid item xs={isRecip ? 3 : 3}>
          <Select value={filteredTrackingType} onChange={onTrackingTypeChange}>
            {getTrackingTypes(criteria.guidelineId).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        </Grid>
        <Grid item xs={isRecip ? 2 : 3}>
          <Select value={selectedLesionNumber} onChange={onLesionNumberChange}>
            <option key={-1} value={-1}>
              ------
            </option>
            {filteredTrackingType === 'selected' &&
              ROIContours.map(roi => (
                <option key={roi.ROINumber} value={roi.ROINumber}>
                  {roi.ROIName}
                </option>
              ))}
          </Select>
        </Grid>
        {/* Volume mode selector — RECIP only */}
        {isRecip && (
          <Grid item xs={2}>
            <Select value={volumeMode} onChange={onVolumeModeChange}>
              <option value="all">All Volume</option>
              <option value="psma-positive">PSMA+ Volume</option>
            </Select>
          </Grid>
        )}
        <Grid item xs={isRecip ? 2 : 3}>
          <Select value={measurementTechnique} onChange={onTechniqueChange}>
            {getMeasurementTechniques().map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        </Grid>
      </Grid>
    </Body>
  );
};
FindingSection.propTypes = {
  imageSets: PropTypes.array,
  ROIContours: PropTypes.array,
  criteria: PropTypes.any,
  assessments: PropTypes.array,
  getFilteredROIContours: PropTypes.func,
  getRoiUnionExcludingPsma: PropTypes.func,
  filteredBodyPart: PropTypes.string,
  setFilteredBodyPart: PropTypes.func,
  filteredTrackingType: PropTypes.string,
  setFilteredTrackingType: PropTypes.func,
  filteredPsmaStatus: PropTypes.string,
  setFilteredPsmaStatus: PropTypes.func,
  selectedLesionNumber: PropTypes.number,
  setSelectedLesionNumber: PropTypes.func,
  measurementTechnique: PropTypes.string,
  setMeasurementTechnique: PropTypes.func,
  volumeMode: PropTypes.string,
  setVolumeMode: PropTypes.func,
};
